/**
 * CriterionRow — one criterion in the Inato-style split-view results list.
 *
 * The verdict label reflects ELIGIBILITY IMPACT, never the raw AI value:
 *   inclusion met → ELIGIBLE (green) · inclusion not_met → NOT ELIGIBLE (red)
 *   exclusion met → NOT ELIGIBLE (red) · exclusion not_met → ELIGIBLE (green)
 *   not_applicable → ELIGIBLE · not applicable (criterion can't apply, e.g.
 *   female-only requirement for a male lead — either kind)
 *   uncertain / contradictory → NEEDS REVIEW · <why> (amber ?)
 *   not_found on an INCLUSION → NEEDS REVIEW · no evidence found (a human must
 *   go find the missing evidence); not_found on an EXCLUSION → ELIGIBLE · no
 *   evidence of exclusion (absence of evidence for an exclusion doesn't block
 *   and needs no confirmation — owner decision 2026-07-11; the backend roll-up
 *   mirrors this via rollupResultValue)
 * Human-overridden rows LEAD with the human verdict (same impact mapping,
 * "HUMAN OVERRIDE" marker) and demote the AI verdict to a small struck chip.
 *
 * COLLAPSED BY DEFAULT: a row shows only the verdict line (+ badges/actions)
 * and the criterion text CLAMPED to two lines (plain-language line to one) —
 * a review with dozens of long protocol criteria stays scannable. The
 * supporting evidence (AI reasoning + confidence, citation chips, human
 * verification details) and the full criterion text live behind a
 * "Show details · N sources" toggle; clicking anywhere non-interactive on the
 * row also toggles it. Citation
 * chips are small source cards ("File 2.pdf · p.5", "SMS · Jun 12") with a
 * quote preview; document citations open the PDF viewer at that page.
 * Citations with a dossier `segment_number` (legacy engine) open the segment
 * text view. Facts-engine citations carry NO segment_number (no dossier
 * exists) — those chips expand IN PLACE to show the already-in-hand excerpt,
 * never triggering a `/segments/:n` fetch.
 *
 * A `resolution_tier` chip (facts engine only) shows how the verdict was
 * reached: "Auto-resolved" (deterministic, code-only) / "AI" (Tier 1) /
 * "AI (deep)" (Tier 2 escalation). legacy/null renders no chip.
 *
 * Actions: [Confirm] one-click verify-agree (POSTs human_result = AI result via
 * the parent's onConfirm) and [Edit] opening the verify/override modal.
 * Verified rows show the verified state + "Edit verification" until sign-off.
 */
import { useState } from 'react';
import {
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  FileText,
  HelpCircle,
  Loader2,
  MessageSquare,
  Pencil,
  Search,
  ShieldCheck,
  Sparkles,
  ThumbsUp,
  User,
  XCircle,
  Zap,
} from 'lucide-react';
import {
  EligibilityCitation,
  EligibilityCriterionResult,
  EligibilityCriterionResultValue,
} from '../../types';
import { Alert } from '../ui';

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

// ---------------------------------------------------------------------------
// Impact mapping — shared by the row, the list panel (sorting/filtering/summary
// chips) and anything else that needs "what does this verdict MEAN".
// ---------------------------------------------------------------------------

export type EligibilityImpact = 'eligible' | 'not_eligible' | 'needs_review';

export interface ImpactVerdict {
  impact: EligibilityImpact;
  /** Bold verdict label, e.g. "ELIGIBLE", "NOT ELIGIBLE", "NEEDS REVIEW". */
  label: string;
  /** Lighter suffix after the label, e.g. "uncertain", "no evidence found". */
  sub?: string;
}

/** The human override when verified, else the AI result. */
export function effectiveResult(r: EligibilityCriterionResult): EligibilityCriterionResultValue {
  return (r.human_verified && r.human_result) || r.result;
}

/** Map a raw verdict + criterion kind to its eligibility impact. */
export function impactOf(
  value: EligibilityCriterionResultValue,
  kind: 'inclusion' | 'exclusion' | undefined,
): ImpactVerdict {
  const exclusion = kind === 'exclusion';
  switch (value) {
    case 'met':
      return exclusion
        ? { impact: 'not_eligible', label: 'NOT ELIGIBLE' }
        : { impact: 'eligible', label: 'ELIGIBLE' };
    case 'not_met':
      return exclusion
        ? { impact: 'eligible', label: 'ELIGIBLE' }
        : { impact: 'not_eligible', label: 'NOT ELIGIBLE' };
    case 'not_applicable':
      // Structurally can't apply to this patient (e.g. a female-only
      // requirement for a male lead) — never blocks, for either kind.
      return { impact: 'eligible', label: 'ELIGIBLE', sub: 'not applicable' };
    case 'uncertain':
      return { impact: 'needs_review', label: 'NEEDS REVIEW', sub: 'uncertain' };
    case 'contradictory':
      return { impact: 'needs_review', label: 'NEEDS REVIEW', sub: 'conflicting evidence' };
    case 'not_found':
      // No evidence of an EXCLUSION keeps the patient eligible — nothing to
      // confirm. No evidence of an INCLUSION is exactly what a human must go
      // find, so it stays a review item.
      return exclusion
        ? { impact: 'eligible', label: 'ELIGIBLE', sub: 'no evidence of exclusion' }
        : { impact: 'needs_review', label: 'NEEDS REVIEW', sub: 'no evidence found' };
    default:
      return { impact: 'needs_review', label: 'NEEDS REVIEW', sub: 'unrecognized result' };
  }
}

export const IMPACT_STYLES: Record<EligibilityImpact, { text: string; icon: typeof CheckCircle }> = {
  eligible: { text: 'text-green-700 dark:text-green-400', icon: CheckCircle },
  not_eligible: { text: 'text-red-700 dark:text-red-400', icon: XCircle },
  needs_review: { text: 'text-amber-600 dark:text-amber-400', icon: HelpCircle },
};

// ---------------------------------------------------------------------------
// Confidence badge (carried over from the old card).
// ---------------------------------------------------------------------------

const CONFIDENCE_STYLES: Record<string, { cls: string; label: string; tip: string }> = {
  high: {
    cls: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800',
    label: 'High confidence',
    tip: 'The AI found clear, unambiguous evidence supporting this evaluation.',
  },
  medium: {
    cls: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/20 dark:text-amber-400 dark:border-amber-800',
    label: 'Medium confidence',
    tip: 'The AI found some evidence, but it may be incomplete, indirect, or require interpretation.',
  },
  low: {
    cls: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800',
    label: 'Low confidence',
    tip: 'The AI found limited or unclear evidence — verify this result carefully against the sources.',
  },
  unable: {
    cls: 'bg-gray-100 text-gray-600 border-gray-300 dark:bg-gray-700/50 dark:text-gray-300 dark:border-gray-600',
    label: 'Unable to assess',
    tip: 'The AI could not assess its confidence for this criterion.',
  },
};

function ConfidenceBadge({ level, score }: { level: string | null; score: number | null }) {
  if (!level) return null;
  const style = CONFIDENCE_STYLES[level] || CONFIDENCE_STYLES.unable;
  const pct = score != null && Number.isFinite(Number(score)) ? `${Math.round(Number(score) * 100)}%` : null;
  return (
    <span
      title={style.tip}
      className={`inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-medium ${style.cls}`}
    >
      {pct ? `${style.label} · ${pct}` : style.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Facts-engine tier chip — how this verdict was reached. Understated styling
// (an outline chip, not a filled badge) so it reads as provenance, not a
// status. legacy/null resolution_tier renders nothing (unchanged today).
// ---------------------------------------------------------------------------

const TIER_META: Record<string, { label: string; icon: typeof Zap; cls: string; tip: string }> = {
  deterministic: {
    label: 'Auto-resolved',
    icon: Zap,
    cls: 'border-cyan-300 text-cyan-700 dark:border-cyan-700 dark:text-cyan-400',
    tip: 'Resolved deterministically from extracted, cited facts — no AI judgment call',
  },
  llm_small: {
    label: 'AI',
    icon: Sparkles,
    cls: 'border-gray-300 text-gray-500 dark:border-gray-600 dark:text-gray-400',
    tip: 'Resolved by an AI judgment call over retrieved facts and record excerpts',
  },
  llm_large: {
    label: 'AI (deep)',
    icon: Sparkles,
    cls: 'border-gray-300 text-gray-500 dark:border-gray-600 dark:text-gray-400',
    tip: 'Resolved by a deeper AI judgment call — escalated for a fuller look at the evidence',
  },
};

function TierChip({ tier }: { tier: string | null | undefined }) {
  if (!tier) return null;
  const meta = TIER_META[tier];
  if (!meta) return null;
  const Icon = meta.icon;
  return (
    <span
      title={meta.tip}
      className={`inline-flex items-center gap-1 rounded-full border bg-transparent px-1.5 py-px text-[9px] font-medium ${meta.cls}`}
    >
      <Icon size={9} />
      {meta.label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Citation chips — small source cards with a quote preview.
// ---------------------------------------------------------------------------

const EVIDENCE_LABELS: Record<string, string> = {
  supports: 'Supports',
  contradicts: 'Contradicts',
  mentions: 'Mentions',
};

const SOURCE_TYPE_LABELS: Record<string, string> = {
  document_page: 'Medical record',
  communication: 'Communication',
  form_response: 'Questionnaire',
  attempt: 'Prescreen attempt',
  profile: 'Medical profile',
  demographics: 'Demographics',
};

const SOURCE_TYPE_ICONS: Record<string, typeof FileText> = {
  document_page: FileText,
  communication: MessageSquare,
  form_response: ClipboardList,
  attempt: ClipboardList,
  profile: User,
  demographics: User,
};

const shortDate = (iso: string | null | undefined): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

/**
 * Dot + icon + label header, shared by both the clickable (jump-to-source)
 * and inline (expand-in-place) chip variants below.
 */
function CitationHeader({ citation, label, locator, Icon }: {
  citation: EligibilityCitation;
  label: string;
  locator: string | null;
  Icon: typeof FileText;
}) {
  return (
    <span className="flex items-center gap-1.5 text-xs font-medium text-primary-700 dark:text-primary-400">
      <span
        className={`h-1.5 w-1.5 shrink-0 rounded-full ${
          citation.evidence_type === 'supports'
            ? 'bg-green-500'
            : citation.evidence_type === 'contradicts'
              ? 'bg-red-500'
              : 'bg-gray-400'
        }`}
      />
      <Icon size={12} className="shrink-0" />
      <span className="truncate">{label}</span>
      {locator && <span className="shrink-0 font-normal text-gray-400">· {locator}</span>}
    </span>
  );
}

export function CitationChip({
  citation,
  documentNames,
  onClick,
}: {
  citation: EligibilityCitation;
  documentNames: Record<string, string>;
  onClick: () => void;
}) {
  const src = citation.source;
  const type = src?.type || '';
  const isDoc = type === 'document_page' && !!src?.document_id;
  // Facts-engine citations have no dossier — segment_number is null. A
  // document_page citation still opens the PDF viewer (existing mechanism,
  // unchanged); everything else with segment_number == null has NO
  // /segments/:n to fetch, so it renders as an expandable inline excerpt
  // instead of a source-jump button. Legacy citations (segment_number set)
  // keep the original jump-to-segment-text behavior.
  const noSegment = citation.segment_number == null;
  const [expanded, setExpanded] = useState(false);

  const Icon = SOURCE_TYPE_ICONS[type] || FileText;
  const label = isDoc
    ? (src?.document_id && documentNames[src.document_id]) || src?.label || 'Medical record'
    : src?.label || SOURCE_TYPE_LABELS[type] || (citation.segment_number != null ? `Segment ${citation.segment_number}` : 'Source');
  const locator = isDoc
    ? src?.page != null ? `p.${src.page}` : null
    : shortDate(src?.occurred_at);

  if (isDoc || !noSegment) {
    return (
      <button
        type="button"
        onClick={onClick}
        title={`${EVIDENCE_LABELS[citation.evidence_type] || citation.evidence_type} — open this source`}
        className="block w-full min-h-[44px] max-w-full rounded-lg border border-gray-200 bg-gray-50 p-2 text-left transition-colors duration-150 hover:border-primary-300 hover:bg-primary-50/60 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-900/40 dark:hover:border-primary-700 dark:hover:bg-primary-900/10 sm:w-auto sm:min-h-0 sm:max-w-[15rem]"
      >
        <CitationHeader citation={citation} label={label} locator={locator} Icon={Icon} />
        {citation.excerpt && (
          <span className="mt-1 line-clamp-3 break-words text-xs leading-snug text-gray-500 dark:text-gray-400">
            “{citation.excerpt}”
          </span>
        )}
      </button>
    );
  }

  // Inline expandable variant — no fetch, no PDF jump; the full excerpt is
  // already in hand (facts-engine citations carry it verbatim).
  return (
    <button
      type="button"
      onClick={() => setExpanded((v) => !v)}
      aria-expanded={expanded}
      title={`${EVIDENCE_LABELS[citation.evidence_type] || citation.evidence_type} — ${expanded ? 'collapse' : 'show'} this source`}
      className="block w-full min-h-[44px] max-w-full rounded-lg border border-gray-200 bg-gray-50 p-2 text-left transition-colors duration-150 hover:border-primary-300 hover:bg-primary-50/60 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-900/40 dark:hover:border-primary-700 dark:hover:bg-primary-900/10 sm:w-auto sm:min-h-0 sm:max-w-[15rem]"
    >
      <span className="flex items-center justify-between gap-1">
        <CitationHeader citation={citation} label={label} locator={locator} Icon={Icon} />
        {citation.excerpt && (expanded ? <ChevronUp size={12} className="shrink-0 text-gray-400" /> : <ChevronDown size={12} className="shrink-0 text-gray-400" />)}
      </span>
      {citation.excerpt && (
        <span className={`mt-1 whitespace-pre-wrap break-words text-xs leading-snug text-gray-500 dark:text-gray-400 ${expanded ? 'block' : 'line-clamp-3'}`}>
          “{citation.excerpt}”
        </span>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

interface Props {
  result: EligibilityCriterionResult;
  /** document_id → filename, for citation chip labels. */
  documentNames: Record<string, string>;
  onCitationClick: (citation: EligibilityCitation) => void;
  /** One-click verify-agree (human_result = AI result). Omitted after sign-off. */
  onConfirm?: (r: EligibilityCriterionResult) => Promise<void>;
  /** Opens the verify/override modal. Omitted after sign-off. */
  onEdit?: (r: EligibilityCriterionResult) => void;
}

export default function CriterionRow({ result, documentNames, onCitationClick, onConfirm, onEdit }: Props) {
  const [expanded, setExpanded] = useState(false);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [rowError, setRowError] = useState<string | null>(null);
  // New reviews return a one-line summary; older reviews stored the full
  // finding-by-finding reasoning. Clamp a long one so it never walls the row.
  const [reasoningExpanded, setReasoningExpanded] = useState(false);
  const reasoningLong = (result.ai_reasoning?.length || 0) > 260;

  const aiVerdict = impactOf(result.result, result.criterion_kind);
  const hasOverride = result.human_verified && !!result.human_result && result.human_result !== result.result;
  const leadVerdict = hasOverride ? impactOf(result.human_result!, result.criterion_kind) : aiVerdict;
  const style = IMPACT_STYLES[leadVerdict.impact];
  const VerdictIcon = style.icon;

  const citations = Array.isArray(result.citations) ? result.citations : [];
  const hasVerificationDetails = result.human_verified && !!(result.human_override_reason || result.human_notes);
  // Everything below the criterion text is collapsed by default — the list
  // stays a scannable verdict-per-line map; evidence is one click away.
  const hasDetails = !!result.ai_reasoning || citations.length > 0 || hasVerificationDetails;

  const confirm = async () => {
    if (!onConfirm || confirmBusy) return;
    setConfirmBusy(true);
    setRowError(null);
    try {
      await onConfirm(result);
    } catch (e: any) {
      setRowError(errMsg(e));
    } finally {
      setConfirmBusy(false);
    }
  };

  // Row-background click toggles the details (and un-clamps the criterion
  // text — every row is toggleable even without evidence); clicks on real
  // controls (buttons, links, citation chips, text inputs) keep their own
  // behavior, and selecting text to copy it is not a toggle.
  const onRowClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if ((e.target as HTMLElement).closest('button, a, input, textarea, select')) return;
    if (window.getSelection()?.toString()) return;
    setExpanded((v) => !v);
  };

  return (
    <div className="cursor-pointer p-3" onClick={onRowClick}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        {/* Verdict line */}
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
          <span className={`inline-flex items-center gap-1.5 text-sm font-bold tracking-wide ${style.text}`}>
            <VerdictIcon size={16} className="shrink-0" />
            {leadVerdict.label}
            {leadVerdict.sub && <span className="font-medium normal-case opacity-80">· {leadVerdict.sub}</span>}
          </span>
          <TierChip tier={result.resolution_tier} />
          {hasOverride && (
            <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide text-amber-800 dark:border-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
              <ShieldCheck size={10} />
              Human override
            </span>
          )}
          {hasOverride && (
            <span
              title="The AI's original verdict — superseded by the human override"
              className="inline-flex items-center rounded-full border border-gray-200 bg-gray-50 px-1.5 py-px text-[10px] font-medium text-gray-500 dark:border-gray-600 dark:bg-gray-700/40 dark:text-gray-400"
            >
              <span className="line-through">
                AI: {aiVerdict.label}
                {aiVerdict.sub ? ` · ${aiVerdict.sub}` : ''}
              </span>
            </span>
          )}
          {result.human_verified && (
            <span className="inline-flex items-center gap-1 rounded-full border border-green-200 bg-green-50 px-1.5 py-px text-[10px] font-medium text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400">
              <ShieldCheck size={10} />
              Verified
            </span>
          )}
          {result.has_contradiction && (
            <span className="inline-flex items-center gap-1 rounded-full border border-purple-200 bg-purple-50 px-1.5 py-px text-[10px] font-medium text-purple-700 dark:border-purple-800 dark:bg-purple-900/20 dark:text-purple-400">
              <AlertTriangle size={10} />
              Conflict
            </span>
          )}
          {result.requires_human_review && !result.human_verified && (
            <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-1.5 py-px text-[10px] font-medium text-amber-700 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-400">
              Needs human review
            </span>
          )}
        </div>

        {/* Actions */}
        {(onConfirm || onEdit) && (
          <div className="flex shrink-0 items-center gap-1.5">
            {onConfirm && !result.human_verified && (
              <button
                type="button"
                onClick={confirm}
                disabled={confirmBusy}
                title="Agree with the AI verdict — records your verification with the same result"
                className="inline-flex min-h-[44px] items-center gap-1 rounded-lg border border-green-200 bg-white px-2.5 py-1.5 text-xs font-medium text-green-700 transition-colors duration-150 hover:bg-green-50 disabled:cursor-not-allowed disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-green-800 dark:bg-gray-800 dark:text-green-400 dark:hover:bg-green-900/20 sm:min-h-0"
              >
                {confirmBusy ? <Loader2 size={13} className="animate-spin" /> : <ThumbsUp size={13} />}
                {confirmBusy ? 'Confirming…' : 'Confirm'}
              </button>
            )}
            {onEdit && (
              <button
                type="button"
                onClick={() => onEdit(result)}
                className="inline-flex min-h-[44px] items-center gap-1 rounded-lg border border-primary-200 bg-white px-2.5 py-1.5 text-xs font-medium text-primary-700 transition-colors duration-150 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-primary-800 dark:bg-gray-800 dark:text-primary-300 dark:hover:bg-primary-900/20 sm:min-h-0"
              >
                <Pencil size={13} />
                {result.human_verified ? 'Edit verification' : 'Edit'}
              </button>
            )}
          </div>
        )}
      </div>

      {rowError && (
        <Alert tone="error" size="sm" icon={false} className="mt-2 break-words">
          {rowError}
        </Alert>
      )}

      {/* Criterion text — clamped to two lines while collapsed so a protocol's
          full multi-sentence criterion doesn't wall the list; expanding the
          row (or "Show details") reveals it whole. */}
      <div className="mt-1.5 flex flex-wrap items-start gap-x-2 gap-y-1">
        {result.criterion_number && (
          <span
            className={`mt-0.5 inline-flex shrink-0 items-center rounded px-1.5 py-0.5 text-[10px] font-bold ${
              result.criterion_kind === 'exclusion'
                ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
                : 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300'
            }`}
          >
            {result.criterion_number}
          </span>
        )}
        <p className={`min-w-0 flex-1 break-words text-sm text-gray-900 dark:text-gray-100 ${expanded ? '' : 'line-clamp-2'}`}>
          {result.criterion_text || '(criterion text unavailable)'}
        </p>
      </div>
      {result.plain_language && (
        <p className={`mt-0.5 break-words text-xs text-gray-500 dark:text-gray-400 ${expanded ? '' : 'line-clamp-1'}`}>
          {result.plain_language}
        </p>
      )}

      {/* Details toggle — evidence stays collapsed so the list scans clean */}
      {hasDetails && (
        <button
          type="button"
          aria-expanded={expanded}
          onClick={() => setExpanded((v) => !v)}
          className="mt-1.5 inline-flex min-h-[44px] items-center gap-1 rounded-lg px-1.5 py-1 text-xs font-medium text-gray-500 transition-colors duration-150 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400 dark:hover:bg-gray-700/50 dark:hover:text-gray-200 sm:min-h-0"
        >
          {expanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          {expanded
            ? 'Hide details'
            : citations.length > 0
              ? `Show details · ${citations.length} source${citations.length === 1 ? '' : 's'}`
              : 'Show details'}
        </button>
      )}

      {expanded && (
        <>
          {/* AI reasoning — indented, left-bordered, muted */}
          {result.ai_reasoning && (
            <div className="ml-1 mt-2 border-l-2 border-gray-200 pl-3 dark:border-gray-600">
              <div className="mb-0.5 flex items-center gap-2">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">AI assessment</span>
                <ConfidenceBadge level={result.confidence_level} score={result.confidence_score} />
              </div>
              <p className={`whitespace-pre-wrap break-words text-sm leading-relaxed text-gray-500 dark:text-gray-400 ${reasoningLong && !reasoningExpanded ? 'line-clamp-4' : ''}`}>
                {result.ai_reasoning}
              </p>
              {reasoningLong && (
                <button
                  type="button"
                  onClick={() => setReasoningExpanded((v) => !v)}
                  className="mt-0.5 text-[11px] font-medium text-primary-700 hover:underline focus:outline-none dark:text-primary-400"
                >
                  {reasoningExpanded ? 'Show less' : 'Show more'}
                </button>
              )}
              {result.result === 'not_found' && (
                <p className="mt-1.5 text-xs text-gray-400 dark:text-gray-500">
                  {result.criterion_kind === 'exclusion'
                    ? 'No evidence of this exclusion was found in this lead\'s sources, so it does not block eligibility. It could still exist in records not on file — spot-check if in doubt.'
                    : 'No evidence was found in this lead\'s sources. That does NOT mean the condition is absent — it may exist in records not on file.'}
                </p>
              )}
            </div>
          )}

          {/* Citation chips */}
          {citations.length > 0 && (
            <div className="mt-2.5 flex flex-wrap gap-2">
              {citations.map((c, i) => (
                <CitationChip key={i} citation={c} documentNames={documentNames} onClick={() => onCitationClick(c)} />
              ))}
            </div>
          )}

          {/* Retrieval transparency — what the AI searched for and what matched.
              Facts-engine rows only (legacy rows have no retrieval_meta). Directly
              answers "why didn't it see X": the terms it looked for, how much
              matched, and which record pages it actually read. */}
          <RetrievalMetaPanel meta={result.retrieval_meta} />

          {/* Human verification details */}
          {hasVerificationDetails && (
            <div className="mt-2.5 rounded-lg border border-green-200 bg-green-50/70 p-2.5 dark:border-green-800 dark:bg-green-900/20">
              <p className="mb-0.5 text-[10px] font-semibold uppercase tracking-wider text-green-700 dark:text-green-400">
                Human verification
              </p>
              <div className="space-y-0.5 text-xs text-gray-700 dark:text-gray-300">
                {result.human_override_reason && (
                  <p className="break-words">
                    <span className="font-medium">Reason:</span> {result.human_override_reason}
                  </p>
                )}
                {result.human_notes && (
                  <p className="break-words">
                    <span className="font-medium">Notes:</span> {result.human_notes}
                  </p>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Retrieval transparency — "What the AI searched" (facts-engine rows only).
// Renders the stored retrieval_meta: the term set the engine matched with, how
// many facts / record excerpts / answers matched, and which record pages the
// evidence came from. Zero matches gets an explicit amber explainer with the
// remedy (add matching search terms on the study page), so "why didn't it see
// X" is answerable from this screen.
// ---------------------------------------------------------------------------

const TERMS_PREVIEW_COUNT = 12;

function RetrievalMetaPanel({ meta }: { meta: EligibilityCriterionResult['retrieval_meta'] }) {
  const [showAllTerms, setShowAllTerms] = useState(false);
  const terms = Array.isArray(meta?.terms) ? meta!.terms!.filter((t) => typeof t === 'string' && t) : [];
  if (!meta || terms.length === 0) return null; // legacy rows / no meta — render nothing

  const factHits = Number(meta.fact_hits) || 0;
  const snippetHits = Number(meta.snippet_hits) || 0;
  const answerHits = Number(meta.answer_hits) || 0;
  const pages = Array.isArray(meta.pages) ? meta.pages.filter((p) => Number.isFinite(Number(p))) : [];
  const nothingMatched = factHits + snippetHits + answerHits === 0;

  const shown = showAllTerms ? terms : terms.slice(0, TERMS_PREVIEW_COUNT);
  const hiddenCount = terms.length - shown.length;

  return (
    <div className="ml-1 mt-2.5 border-l-2 border-gray-200 pl-3 dark:border-gray-600">
      <div className="mb-1 flex items-center gap-1.5">
        <Search size={11} className="text-gray-400" />
        <span className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">What the AI searched</span>
      </div>
      <div className="flex flex-wrap items-center gap-1">
        {shown.map((t) => (
          <span
            key={t}
            className="inline-flex items-center rounded border border-gray-200 bg-gray-50 px-1.5 py-px text-[10px] text-gray-600 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-300"
          >
            {t}
          </span>
        ))}
        {hiddenCount > 0 && (
          <button
            type="button"
            onClick={() => setShowAllTerms(true)}
            className="text-[10px] font-medium text-primary-700 hover:underline focus:outline-none dark:text-primary-400"
          >
            +{hiddenCount} more
          </button>
        )}
        {showAllTerms && terms.length > TERMS_PREVIEW_COUNT && (
          <button
            type="button"
            onClick={() => setShowAllTerms(false)}
            className="text-[10px] font-medium text-primary-700 hover:underline focus:outline-none dark:text-primary-400"
          >
            show fewer
          </button>
        )}
      </div>
      <p className="mt-1 text-[11px] text-gray-500 dark:text-gray-400">
        Matched {factHits} extracted fact{factHits === 1 ? '' : 's'} · {snippetHits} record excerpt{snippetHits === 1 ? '' : 's'} · {answerHits} answer{answerHits === 1 ? '' : 's'}
        {pages.length > 0 && (
          <> · from record page{pages.length === 1 ? '' : 's'} {pages.join(', ')}</>
        )}
      </p>
      {nothingMatched && (
        <p className="mt-1 flex items-start gap-1 text-[11px] text-amber-700 dark:text-amber-400">
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
          None of these terms matched anything in the sources this review read. If you know this
          information is on file, add the wording the record actually uses as a search term on this
          criterion (study page → review criteria), then run a new review.
        </p>
      )}
    </div>
  );
}
